import { addDays, endOfDay } from 'date-fns'

import { AppDispatch } from '@app/store/store'
import catchErrorHandler from '@app/store/utils/catchErrorHandler'
import { apiSdk } from '@app/api/endpoints'
import { Field } from '@app/api/types'
import {
  CheckIfZipCodeIsSupportedMutation,
  CreateLeadInput,
  GetAvailableTimeSlotsQuery,
  GetCurrentTimeUtcQuery,
  ZipCodeIsSupportedInput
} from '@app/graphql/service/schema'
import { runtimeConfig } from '@app/common/config'
import { useCheckSetFieldValueResponse } from '@app/hooks/useCheckSetFieldValueResponse'
import { BookingSteps } from '@app/store/booking'
import { addNotification, NotificationActionType } from '@app/store/notifications'
import { ERROR_MESSAGES } from '@app/constants/booking'

import { actions } from './slice'

const CHANNEL_ID = runtimeConfig.joinSproutTherapyConfig?.CHANNEL_ID
export const BOOKING_NOTIFICATION_AUTO_CLOSE_TIME = 30000

export const getAvailableTimeSlotsAction = (sessionId: string) => async (dispatch: AppDispatch) => {
  try {
    const dateFrom = endOfDay(new Date())
    const dateTo = endOfDay(addDays(dateFrom, 7))

    const rawDates: GetAvailableTimeSlotsQuery = await apiSdk.GetAvailableTimeSlots({
      channelId: CHANNEL_ID,
      sessionId,
      fromDate: dateFrom.toISOString(),
      toDate: dateTo.toISOString()
    })

    const rawTimeSlots = rawDates.channels?.[0]?.sessions?.[0]?.timeSlots
    const timeSlots = rawTimeSlots ? rawTimeSlots.map(rawTimeSlot => new Date(rawTimeSlot?.datetime).getTime()) : []
    dispatch(
      actions.setTimeSlots({ dateFrom: new Date(dateFrom).getTime(), dateTo: new Date(dateTo).getTime(), timeSlots })
    )
  } catch (error) {
    catchErrorHandler(error, dispatch, actions.errorBackgroundRequest)
    dispatch(
      addNotification({
        autoCloseTime: BOOKING_NOTIFICATION_AUTO_CLOSE_TIME,
        message: ERROR_MESSAGES.backgroundRequest,
        actionType: NotificationActionType.Reload
      })
    )
  }
}

export const getCurrentUtcTime = () => async (dispatch: AppDispatch) => {
  try {
    const result: GetCurrentTimeUtcQuery = await apiSdk.GetCurrentTimeUtc()
    dispatch(actions.setCurrentUtcTime(result.current_time_utc))
  } catch (error) {
    console.error(error.message)
    const currentUtcTime = new Date().toISOString()
    dispatch(actions.setCurrentUtcTime(currentUtcTime))
  }
}

export const updateValue = (sessionId: string, field: Field) => async (dispatch: AppDispatch) => {
  dispatch(actions.requestUpdateBookingInputData())

  try {
    if (field.apiFieldKey) {
      const response = await apiSdk.SetFieldValue({
        channelId: CHANNEL_ID,
        sessionId,
        field: field.apiFieldKey,
        value: field.fieldValue
      })

      useCheckSetFieldValueResponse(response)
    }

    dispatch(actions.successUpdateBookingInputData(field))
  } catch (error) {
    catchErrorHandler(error, dispatch, actions.errorUpdateBookingInputData)
  }
}

export const checkIfZipCodeIsSupported = (data: ZipCodeIsSupportedInput) => async (dispatch: AppDispatch) => {
  dispatch(actions.requestUpdateBookingInputData())

  try {
    const response: CheckIfZipCodeIsSupportedMutation = await apiSdk.CheckIfZipCodeIsSupported({ data })
    dispatch(
      actions.successUpdateBookingInputData({
        fieldName: 'isZipCodeSupported',
        fieldValue: `${response.zipCode.isSupported.isSupported}`
      })
    )
  } catch (error) {
    catchErrorHandler(error, dispatch, actions.errorUpdateBookingInputData)
  }
}

export const createLead = (inputData: CreateLeadInput) => async (dispatch: AppDispatch) => {
  dispatch(actions.requestCreateLead())
  try {
    const response = await apiSdk.CreateLead({ input: inputData })
    if (response.createLead.__typename === 'CreateLeadError') {
      throw new Error(response.createLead.code)
    }
    dispatch(actions.successCreateLead())
  } catch (error) {
    catchErrorHandler(error, dispatch, actions.errorCreateLead)
  }
}

export const confirmBooking = (sessionId: string) => async (dispatch: AppDispatch) => {
  dispatch(actions.requestConfirmBooking())
  try {
    await apiSdk.ConfirmBooking({
      channelId: CHANNEL_ID,
      sessionId
    })
    dispatch(actions.successConfirmBooking())
  } catch (error) {
    catchErrorHandler(error, dispatch, actions.errorConfirmBooking)
    throw new Error(error.message)
  }
}

export const setBookingStep = (step: BookingSteps) => async (dispatch: AppDispatch) => {
  try {
    dispatch(actions.setBookingStep(step))
  } catch (error) {
    console.error(error.message)
  }
}
